import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  ArrowLeft, 
  ArrowRight,
  Clock,
  Calendar,
  User,
  Tag,
  Share2,
  BookOpen,
  ChevronRight,
  ChevronUp,
  Facebook,
  Twitter,
  Linkedin,
  Copy,
  Check,
  Mail,
  Sparkles,
  TrendingUp,
  BookMarked,
  MessageCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";
import { blogPosts, getPostBySlug, getRelatedPosts } from "../data/blogPosts";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const relatedPosts = post ? getRelatedPosts(slug, 3) : [];
  const [copied, setCopied] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);

  // Reading progress and scroll to top
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setReadingProgress(progress);
      setShowScrollTop(scrollTop > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center bg-white p-12 rounded-2xl shadow-lg">
          <BookOpen className="w-20 h-20 text-slate-300 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-slate-900 mb-3">Article Not Found</h1>
          <p className="text-slate-600 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="bg-[#00B894] hover:bg-[#00A085] text-white px-8">
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleShare = async (platform) => {
    const url = window.location.href;
    const text = `${post.title} - ${post.excerpt}`;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(text + '\n\n' + url)}`
    };

    if (platform === 'copy') {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const pageTitle = `${post.title} | GigsEasy Blog`;
  const canonicalUrl = `https://gigseasy.com/blog/${post.slug}`;
  const keywordsString = post.keywords.join(", ");

  // Get popular posts (featured ones)
  const popularPosts = blogPosts.filter(p => p.featured && p.slug !== slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={keywordsString} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="article:published_time" content={post.publishDate} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        <meta property="article:tag" content={keywordsString} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.metaDescription} />
      </Helmet>

      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-slate-200 z-[60]">
        <div 
          className="h-full bg-gradient-to-r from-[#00B894] to-[#0984E3] transition-all duration-150"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-1 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Blog</span>
          </Link>
          <Link to="/" className="hidden sm:block">
            <span className="text-2xl font-bold bg-gradient-to-r from-[#00B894] to-[#0984E3] bg-clip-text text-transparent">
              GigsEasy
            </span>
          </Link>
          <Link to="/register">
            <Button className="bg-[#00B894] hover:bg-[#00A085] text-white">
              Start Earning
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section with Gradient Background */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }} />
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#00B894]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#0984E3]/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-8">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300 truncate max-w-xs">{post.category}</span>
          </div>

          <div className="max-w-4xl">
            {/* Category & Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="text-sm font-semibold bg-[#00B894] text-white px-4 py-1.5 rounded-full">
                {post.category}
              </span>
              <span className="text-sm text-slate-400 flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
              <span className="text-sm text-slate-400 flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {new Date(post.publishDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Excerpt */}
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-8">
              {post.excerpt}
            </p>

            {/* Author */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-white">{post.author}</p>
                <p className="text-sm text-slate-400">Content Writer at GigsEasy</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area with Sidebar */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-[1fr_340px] gap-12">
          
          {/* Main Article */}
          <article className="min-w-0">
            {/* Article Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              {/* Content */}
              <div className="p-8 lg:p-12">
                <div className="prose prose-lg max-w-none 
                  prose-headings:font-bold prose-headings:text-slate-900 prose-headings:scroll-mt-24
                  prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-3 prose-h2:border-b prose-h2:border-slate-200
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
                  prose-p:text-slate-600 prose-p:leading-relaxed
                  prose-a:text-[#00B894] prose-a:font-medium prose-a:no-underline hover:prose-a:underline 
                  prose-strong:text-slate-900 prose-strong:font-semibold
                  prose-ul:text-slate-600 prose-ol:text-slate-600 prose-li:text-slate-600 prose-li:my-2
                  prose-table:text-sm prose-table:border prose-table:border-slate-200 prose-table:rounded-lg prose-table:overflow-hidden
                  prose-th:bg-slate-100 prose-th:p-4 prose-th:text-left prose-th:font-semibold prose-th:text-slate-900
                  prose-td:p-4 prose-td:border-t prose-td:border-slate-200
                  prose-code:bg-slate-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:text-slate-800
                  prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-pre:rounded-xl
                  prose-blockquote:border-l-4 prose-blockquote:border-[#00B894] prose-blockquote:bg-slate-50 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
                  prose-img:rounded-xl prose-img:shadow-lg">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
                </div>
              </div>

              {/* Tags Section */}
              <div className="px-8 lg:px-12 py-8 bg-slate-50 border-t border-slate-200">
                <div className="flex items-start gap-3">
                  <Tag className="w-5 h-5 text-slate-400 mt-1 flex-shrink-0" />
                  <div className="flex items-center gap-2 flex-wrap">
                    {post.keywords.map((keyword, index) => (
                      <span 
                        key={index}
                        className="text-sm bg-white text-slate-600 px-4 py-1.5 rounded-full border border-slate-200 hover:border-[#00B894] hover:text-[#00B894] transition-colors cursor-pointer"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Share Section */}
              <div className="px-8 lg:px-12 py-8 border-t border-slate-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Share2 className="w-5 h-5" />
                    <span className="font-medium">Share this article</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleShare('twitter')}
                      className="p-3 rounded-full bg-slate-100 hover:bg-[#1DA1F2] hover:text-white transition-all"
                      title="Share on Twitter"
                    >
                      <Twitter className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleShare('facebook')}
                      className="p-3 rounded-full bg-slate-100 hover:bg-[#4267B2] hover:text-white transition-all"
                      title="Share on Facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleShare('linkedin')}
                      className="p-3 rounded-full bg-slate-100 hover:bg-[#0077B5] hover:text-white transition-all"
                      title="Share on LinkedIn"
                    >
                      <Linkedin className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleShare('email')}
                      className="p-3 rounded-full bg-slate-100 hover:bg-slate-700 hover:text-white transition-all"
                      title="Share via Email"
                    >
                      <Mail className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleShare('copy')}
                      className={`p-3 rounded-full transition-all ${copied ? 'bg-green-500 text-white' : 'bg-slate-100 hover:bg-slate-200'}`}
                      title="Copy link"
                    >
                      {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Author Bio Card */}
            <div className="mt-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-2xl flex items-center justify-center flex-shrink-0">
                  <User className="w-10 h-10 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-400 mb-1">Written by</p>
                  <h3 className="text-xl font-bold mb-2">{post.author}</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    The GigsEasy Team is dedicated to helping people earn money online through legitimate opportunities. 
                    We share tips, guides, and insights to maximize your earning potential.
                  </p>
                </div>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-8">
            {/* Sticky Container */}
            <div className="lg:sticky lg:top-24 space-y-8">
              
              {/* Newsletter Signup */}
              <div className="bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-2xl p-6 text-white">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5" />
                  <span className="font-semibold">Get Earning Tips</span>
                </div>
                <p className="text-white/90 text-sm mb-4">
                  Join 10,000+ readers getting weekly tips on making money online.
                </p>
                <Link to="/register">
                  <Button className="w-full bg-white text-[#00B894] hover:bg-slate-100 font-semibold">
                    Start Earning Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>

              {/* Popular Articles */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                  <TrendingUp className="w-5 h-5 text-[#00B894]" />
                  <h3 className="font-bold text-slate-900">Popular Articles</h3>
                </div>
                <div className="space-y-4">
                  {popularPosts.map((popularPost, index) => (
                    <Link 
                      key={popularPost.id}
                      to={`/blog/${popularPost.slug}`}
                      className="flex gap-4 group"
                    >
                      <span className="text-2xl font-bold text-slate-200 group-hover:text-[#00B894] transition-colors">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div>
                        <h4 className="font-medium text-slate-900 group-hover:text-[#00B894] transition-colors line-clamp-2 text-sm">
                          {popularPost.title}
                        </h4>
                        <span className="text-xs text-slate-500">{popularPost.readTime}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                  <BookMarked className="w-5 h-5 text-[#00B894]" />
                  <h3 className="font-bold text-slate-900">Article Info</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600 text-sm">Category</span>
                    <span className="text-sm font-medium text-[#00B894]">{post.category}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600 text-sm">Read Time</span>
                    <span className="text-sm font-medium text-slate-900">{post.readTime}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600 text-sm">Published</span>
                    <span className="text-sm font-medium text-slate-900">
                      {new Date(post.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Help Card */}
              <div className="bg-slate-100 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="w-5 h-5 text-slate-600" />
                  <h3 className="font-semibold text-slate-900">Need Help?</h3>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  Have questions about earning with GigsEasy? We're here to help.
                </p>
                <Link to="/faq">
                  <Button variant="outline" className="w-full">
                    Visit FAQ
                  </Button>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* CTA Banner */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00B894]/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#0984E3]/30 rounded-full blur-3xl" />
        
        <div className="relative max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Start Earning?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Join thousands of people who are already earning with GigsEasy. 
            Put what you've learned into action!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-[#00B894] hover:bg-[#00A085] text-white px-8 w-full sm:w-auto">
                Create Free Account
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/how-to-earn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 w-full sm:w-auto">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-2xl font-bold text-slate-900">Related Articles</h2>
              <Link to="/blog" className="text-[#00B894] hover:underline font-medium flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map(relatedPost => (
                <Link 
                  key={relatedPost.id} 
                  to={`/blog/${relatedPost.slug}`}
                  className="group"
                >
                  <div className="bg-gradient-to-br from-[#00B894]/10 to-[#0984E3]/10 rounded-2xl h-48 flex items-center justify-center mb-4 group-hover:from-[#00B894]/20 group-hover:to-[#0984E3]/20 transition-all">
                    <BookOpen className="w-16 h-16 text-[#00B894]/40 group-hover:scale-110 transition-transform" />
                  </div>
                  <span className="text-xs font-semibold text-[#00B894] uppercase tracking-wide">
                    {relatedPost.category}
                  </span>
                  <h3 className="text-lg font-bold text-slate-900 mt-2 mb-2 group-hover:text-[#00B894] transition-colors line-clamp-2">
                    {relatedPost.title}
                  </h3>
                  <p className="text-slate-600 text-sm line-clamp-2">{relatedPost.excerpt}</p>
                  <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {relatedPost.readTime}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(relatedPost.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-4 bg-[#00B894] text-white rounded-full shadow-lg hover:bg-[#00A085] transition-all z-50"
          aria-label="Scroll to top"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}

      <Footer />
    </div>
  );
}
